// src/pages/EmployeePage/EmployeePage.js
import React, { useState, useEffect } from "react";
import { useNavigate, Routes, Route, useLocation } from "react-router-dom";
import { OrbitLoaderComponent } from "../../utils/orbitLoader";
import {
  getEmployeePersonalInfo,
  getUser,
  createPermissionRequest,
  getEmployeePermissions,
} from "../../services/apiService";
import AttendanceFlow from "../../components/AttendanceFlow/AttendanceFlow";
import LoadingButton from "../../components/LoadingButton/LoadingButton";
import WeeklySummary from "./WeeklySummary";
import MonthlyStatus from "./MonthlyStatus";
import PersonalInfo from "./PersonnelInfo";
import EmployeeLeaveRequest from "../../components/EmployeeLeaveRequest/EmployeeLeaveRequest";
import MaintenancePanelSwitcher from "../../components/MaintenancePanelSwitcher/MaintenancePanelSwitcher";
import "./EmployeePage.css";

const EmployeePage = ({ inMaintenance = false }) => {
  const [loading, setLoading] = useState(true);
  const [personalInfo, setPersonalInfo] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [apiError, setApiError] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [formData, setFormData] = useState({
    permission_type: "",
    start_date: "",
    end_date: "",
    reason: "",
  });
  const [formLoading, setFormLoading] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = getUser();
  const userId = userInfo?.user_id;

  // URL'den sayfayı belirle (sadece maintenance modunda değilken)
  useEffect(() => {
    if (!inMaintenance) {
      const path = location.pathname.split("/").pop();
      if (
        [
          "weekly-summary",
          "monthly-status",
          "personal-info",
          "leave-request",
          "attendance",
        ].includes(path)
      ) {
        // Do nothing, let the route handle it
      }
    }
  }, [location, inMaintenance]);

  useEffect(() => {
    const fetchEmployeeData = async () => {
      if (userInfo && userInfo.name) {
        setPersonalInfo(userInfo);
        setLoading(false);
        return;
      }

      if (!userId) {
        setApiError("Geçerli bir kullanıcı ID bulunamadı");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setApiError(null);
        console.log("Çalışan bilgileri çekiliyor, userId:", userId);
        const data = await getEmployeePersonalInfo(userId);
        if (data && data.name) {
          setPersonalInfo(data);
          console.log("Çalışan bilgileri başarıyla yüklendi:", data);
        } else {
          console.error("API'den eksik kullanıcı bilgileri geldi:", data);
          setApiError(
            "Kullanıcı bilgileri alınamadı. Lütfen daha sonra tekrar deneyin."
          );
        }
      } catch (error) {
        console.error("Çalışan veri çekme hatası:", error);
        if (error.message.includes("401") || error.message.includes("403")) {
          setApiError("Oturum süresi dolmuş. Lütfen tekrar giriş yapın.");
          setTimeout(() => {
            navigate("/login");
          }, 2000);
        } else {
          setApiError("Sunucu hatası: " + (error.message || "Bilinmeyen hata"));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeData();
    fetchPermissions();
  }, [userId, refreshKey, navigate]);

  const fetchPermissions = async () => {
    try {
      const data = await getEmployeePermissions(userId);
      setPermissions(data);
    } catch (error) {
      console.error("İzinler yüklenemedi:", error.message);
    }
  };

  const getDisplayName = () => {
    if (personalInfo && personalInfo.name) {
      return personalInfo.name;
    }
    if (userInfo && userInfo.name) {
      return userInfo.name;
    }
    return "Personel";
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const result = await createPermissionRequest({
        ...formData,
        user_id: userId,
      });
      setNotification({
        show: true,
        message: "İzin talebiniz başarıyla oluşturuldu",
        type: "success",
      });
      setShowPermissionModal(false);
      fetchPermissions();
      setFormData({
        permission_type: "",
        start_date: "",
        end_date: "",
        reason: "",
      });
    } catch (error) {
      setNotification({
        show: true,
        message: "İzin talebi oluşturulamadı: " + error.message,
        type: "error",
      });
    } finally {
      setFormLoading(false);
      setTimeout(() => {
        setNotification({ show: false, message: "", type: "" });
      }, 3000);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("tr-TR");
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: "pending", text: "Bekliyor" },
      approved: { class: "approved", text: "Onaylandı" },
      rejected: { class: "rejected", text: "Reddedildi" },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`status-badge ${config.class}`}>{config.text}</span>
    );
  };

  if (apiError) {
    return (
      <div className="employee-page">
        <div className="employee-container">
          <div className="error-message">
            <p>{apiError}</p>
            <button
              className="retry-button"
              onClick={() => setRefreshKey((prev) => prev + 1)}
            >
              Tekrar Dene
            </button>
            <button className="retry-button" onClick={() => navigate("/login")}>
              Giriş Yap
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-container">
        <OrbitLoaderComponent size="medium" showPath={true} />
        <p className="loading-text">Yükleniyor...</p>
      </div>
    );
  }

  // Eğer maintenance modunda ise, Header'ı gösterme
  if (inMaintenance) {
    return (
      <div className="employee-page">
        <div className="employee-container">
          {notification.show && (
            <div className={`notification ${notification.type}`}>
              {notification.message}
            </div>
          )}
          <Routes>
            <Route
              path="/"
              element={
                <AttendanceFlow
                  userId={userId}
                  key={refreshKey}
                  onError={(error) => setApiError(error)}
                />
              }
            />
            <Route path="weekly-summary" element={<WeeklySummary />} />
            <Route path="monthly-status" element={<MonthlyStatus />} />
            <Route path="personal-info" element={<PersonalInfo />} />
            <Route path="leave-request" element={<EmployeeLeaveRequest />} />
            <Route
              path="attendance"
              element={
                <AttendanceFlow
                  userId={userId}
                  key={refreshKey}
                  onError={(error) => setApiError(error)}
                />
              }
            />
          </Routes>
        </div>
      </div>
    );
  }

  // Normal EmployeePage render'i
  return (
    <div className="employee-page">
      {/* Maintenance Admin için Panel Geçiş Butonu - Sadece normal modda göster */}
      {!inMaintenance && <MaintenancePanelSwitcher />}

      <div className="employee-container">
        <div className="page-header">
          <h2>Ana Sayfa</h2>
          <div className="header-welcome">
            <h3>Hoş Geldiniz {getDisplayName()} Hocam</h3>
          </div>
        </div>

        {notification.show && (
          <div className={`notification ${notification.type}`}>
            {notification.message}
          </div>
        )}

        <Routes>
          <Route
            path="/"
            element={
              <AttendanceFlow
                userId={userId}
                key={refreshKey}
                onError={(error) => setApiError(error)}
              />
            }
          />
          <Route path="weekly-summary" element={<WeeklySummary />} />
          <Route path="monthly-status" element={<MonthlyStatus />} />
          <Route path="personal-info" element={<PersonalInfo />} />
          <Route path="leave-request" element={<EmployeeLeaveRequest />} />
          <Route
            path="attendance"
            element={
              <AttendanceFlow
                userId={userId}
                key={refreshKey}
                onError={(error) => setApiError(error)}
              />
            }
          />
        </Routes>
      </div>
    </div>
  );
};

export default EmployeePage;
